import React, { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { MapPin, Calendar, ExternalLink } from "lucide-react";

function isUpcoming(dateStr) {
  if (!dateStr) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  // Accept "YYYY-MM-DD" primarily; fall back to Date parsing.
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  return d.getTime() >= now.getTime();
}

function formatItalianDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
}

function EventCard({ e, upcoming }) {
  return (
    <article className="card-flat overflow-hidden !p-0" data-testid={`event-${e.id}`}>
      <div className="aspect-[4/3] overflow-hidden bg-lilla relative">
        {e.cover_url ? (
          <img
            src={e.cover_url}
            alt={e.title}
            className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-lilla to-arancio/40" />
        )}
        {upcoming && (
          <span
            className="absolute top-3 left-3 rounded-full bg-fucsia text-white px-3 py-1 text-[10px] font-semibold uppercase tracking-widest shadow-md"
            data-testid={`event-badge-${e.id}`}
          >
            Prossimo evento
          </span>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-display text-2xl leading-tight">{e.title}</h3>
        <p className="mt-3 text-sm text-viola/75 line-clamp-3">{e.description}</p>
        <div className="mt-4 space-y-1 text-sm text-viola/70">
          {e.place && (
            <p className="flex items-center gap-2"><MapPin size={14} /> {e.place}</p>
          )}
          {e.date && (
            <p className="flex items-center gap-2"><Calendar size={14} /> {formatItalianDate(e.date)}</p>
          )}
        </div>
        {e.external_url && (
          <a
            href={e.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-fucsia hover:underline"
            data-testid={`event-link-${e.id}`}
          >
            Scopri di più <ExternalLink size={12} />
          </a>
        )}
      </div>
    </article>
  );
}

export default function Eventi() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/events").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const { upcoming, past } = useMemo(() => {
    const up = [];
    const pa = [];
    for (const e of items) {
      if (isUpcoming(e.date)) up.push(e); else pa.push(e);
    }
    // Upcoming: earliest first. Past: most recent first (already sorted DESC).
    up.sort((a, b) => new Date(a.date) - new Date(b.date));
    return { upcoming: up, past: pa };
  }, [items]);

  return (
    <div className="container-editorial py-16 lg:py-24" data-testid="page-eventi">
      <div className="max-w-3xl">
        <span className="pill">Eventi</span>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter leading-[0.95]">Workshop, conferenze, festival.</h1>
        <p className="mt-6 text-lg text-viola/75">Una selezione di eventi pubblici e workshop a cui partecipo — come relatricə, formatricə o ospite.</p>
      </div>

      {loading ? (
        <p className="mt-16 text-viola/60" data-testid="events-loading">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="mt-16 rounded-2xl bg-lilla p-12 text-center" data-testid="events-empty">
          <p className="font-display text-2xl">Presto la galleria degli eventi.</p>
          <p className="mt-3 text-viola/70">Iscriviti alla newsletter per non perdere i prossimi.</p>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="mt-16" data-testid="events-upcoming-section">
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Prossimi eventi</h2>
                <span className="text-xs uppercase tracking-widest text-viola/60">{upcoming.length} in agenda</span>
              </div>
              <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcoming.map((e) => <EventCard key={e.id} e={e} upcoming />)}
              </div>
            </section>
          )}

          {past.length > 0 && (
            <section className="mt-20" data-testid="events-past-section">
              <div className="flex items-baseline justify-between">
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Eventi passati</h2>
                <span className="text-xs uppercase tracking-widest text-viola/60">Archivio</span>
              </div>
              <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {past.map((e) => <EventCard key={e.id} e={e} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
